import {
  animate,
  element,
  IAugmentedJQuery,
  ICompileService,
  IControllerService,
  IDocumentService,
  IPromise,
  IQResolveReject,
  IQService,
  IRootScopeService,
  ITimeoutService,
  IWindowService
} from 'angular';
import { IModalOpened, IModalOptions, IModalScope, Modal } from './shared';
import { kebabCase } from 'lodash-es';

export class ModalService {
  static $inject = [
    '$window',
    '$q',
    '$document',
    '$compile',
    '$controller',
    '$animate',
    '$timeout',
    '$rootScope'
  ];

  private modalIdCounter = 0;
  private $bodyElem: IAugmentedJQuery;
  private $backdropElem = element('<div>').addClass('modal-backdrop fade');
  private openedModals: Map<number, IModalOpened> = new Map();

  constructor( private $window: IWindowService,
               private $q: IQService,
               private $document: IDocumentService,
               private $compile: ICompileService,
               private $controller: IControllerService,
               private $animate: animate.IAnimateService,
               private $timeout: ITimeoutService,
               private $rootScope: IRootScopeService ) {
    this.init();
  }

  showModal( options: IModalOptions ): IPromise<Modal> {
    const modalId = this.modalIdCounter += 1;
    let closeResolve: IQResolveReject<any>;
    let closedResolve: IQResolveReject<any>;

    const closePromise = this.$q(( resolve ) => closeResolve = resolve);
    const closedPromise = this.$q(( resolve ) => closedResolve = resolve);

    return this.$q.resolve()
      .then(() => {
        const modalScope = this.$rootScope.$new() as IModalScope;
        modalScope.close = ( result: any, delay: number = 0 ) => {
          this.$timeout(() => {
            this.closeModalById(modalId, result);
          }, delay);
        };

        modalScope.data = options.data;
        modalScope.backdrop = options.backdrop;
        modalScope.extraClasses = options.extraClasses;
        const $modalWindow = element('<gmc-modal>')
          .attr('backdrop', 'backdrop')
          .attr('on-close', 'close(result,delay)')
          .attr('extra-classes', 'extraClasses')
          .addClass(`modal d-block angular-animate ${options.extraBackdrop ? 'modal-extra-backdrop': ''}`);
        const $modalContent = element(`<${kebabCase(options.component)}>`)
          .attr('on-close', 'close(result,delay)')
          .attr('data', 'data');

        $modalWindow.append($modalContent);
        const $modalElement = this.$compile($modalWindow)(modalScope);
        this.appendModalIntoDOM(this.$bodyElem, $modalElement);
        const modal = new Modal(null, modalScope, $modalElement, closePromise, closedPromise);
        element(this.$document[ 0 ].activeElement).blur();
        this.appendPaddingToBody();
        this.$bodyElem.addClass('modal-open');
        this.openedModals.set(modalId, { modal, close: closeResolve, closed: closedResolve });
        return modal;
      });
  }

  private closeModalById( modalId: number, result: any ) {
    const openedModal = this.openedModals.get(modalId);
    openedModal.close(result);
    if ( this.openedModals.size === 1 ) {
      this.$backdropElem.removeClass('show');
    }
    this.$animate.leave(openedModal.modal.$element, { removeClass: 'active' })
      .then(() => {
        openedModal.closed(result);
        openedModal.modal.scope.$destroy();
        this.openedModals.delete(modalId);
        if ( this.openedModals.size < 1 ) {
          this.$backdropElem.remove();
          this.$bodyElem.removeClass('modal-open');
          this.$bodyElem.css('padding-right', '');
          this.modalIdCounter = 0;
        }
      });
  }

  private appendModalIntoDOM( parent: IAugmentedJQuery, child: IAugmentedJQuery ) {
    if ( this.openedModals.size === 0 ) {
      this.$bodyElem.append(this.$backdropElem);
    }
    const children = parent.children();
    this.$animate.enter(child, parent, element(children[ children.length - 1 ]))
      .then(() => {
        child.addClass('active');
        this.$backdropElem.addClass('show');
      });
  }

  private init() {
    this.$bodyElem = this.$document.find('body');
    this.$rootScope.$on('$locationChangeSuccess', () => {
      Array.from(this.openedModals.keys()).forEach(( modalId ) => {
        this.closeModalById(modalId, 'location change');
      });
    });
  }

  private appendPaddingToBody() {
    const paddingVal = this.$window.innerWidth - this.$bodyElem.width();
    if ( paddingVal > 0 ) {
      this.$bodyElem.css('padding-right', `${paddingVal}px`);
    }
  }
}



// WEBPACK FOOTER //
// ./app/components/modals/gmc-modal/gmc-modal.service.ts